/**
 * `draftGuard` — is the previous step actually done?
 *
 * Stops `/migrare/plata` from rendering a payment screen for a number nobody
 * verified, and `/migrare/telefoane` from opening on a SIM-only draft. A bookmark, a
 * shared link, a browser back button after a draft was cleared — all three arrive as
 * a deep link into the middle of a wizard, and the flow has nine screens whose
 * content is meaningless without the ones before them.
 *
 * ### It redirects. It does not block
 *
 * The fourteen `BlockReason`s are eligibility answers: who you are, what your company
 * is, whether the number can move, whether the device is in stock. None of them means
 * "you skipped a step", and adding a fifteenth to cover it would be inventing a
 * customer-facing rule to describe a navigation accident. CLAUDE.md is explicit about
 * that, and the block taxonomy is one of the artefacts the POC exists to keep honest.
 *
 * So this is POC navigation hygiene and nothing more: send the customer to the screen
 * where the missing thing is supplied. No explanation panel, because no source says
 * what the customer should be told — or whether they should be told anything, since
 * in the specified journeys this situation cannot arise.
 *
 * ### Two sources, and only one of them is free to read
 *
 * `MigrationDraftStore` holds the variant, the offer, the number, its verification
 * and the device chosen but not yet added — everything the journey decides before a
 * cart exists, which is everything up to S-06. Reading it is synchronous.
 *
 * From S-07 onwards the prerequisite is on the cart, and the cart comes over the
 * wire. So the guard is asynchronous, and awaits `CartStore.ensureLoaded()` — but
 * only for the screens that need it. S-03 through S-06 must not pay a round trip to
 * check a condition that is not theirs, which is why the requested screen decides
 * whether the await happens rather than the guard always fetching.
 *
 * A cart-backed screen requested before the cart has loaded reads `simOption()` as
 * null, which is the same answer as "not chosen". That is why the await is required
 * and not an optimisation: without it, S-07 would bounce to S-06 on every deep link.
 *
 * ### What it still does not check
 *
 * Not `violations` — `Cart.violations` is never empty in this POC (`POC-PREPAID-CREDIT`,
 * Q-28), so a guard that treated it as a stop condition would stop the happy path.
 * See `CartStore`'s header.
 *
 * It does check the payment method and the delivery address for S-09, which it did
 * not while S-08 was unbuilt: those are exactly the two things
 * `OrderService.assertCartComplete` rejects a placement for, so the condition is read
 * off the mutation rather than invented here.
 */
import { inject } from '@angular/core';
import { RedirectCommand, Router } from '@angular/router';
import type { CanActivateFn } from '@angular/router';
import { AccessBlockStore } from '../access/access-block.store';
import { ROUTE_FOR, previousScreen, screenOrder } from '../flow/flow-steps';
import type { FlowScreenId } from '../flow/flow-steps';
import type { PageMeta } from '../flow/page-meta';
import { CartStore } from '../stores/cart.store';
import { MigrationDraftStore } from '../stores/migration-draft.store';

/**
 * The screens whose prerequisite lives on the cart, and therefore the only ones
 * this guard will wait for a network read to decide. S-06 is absent on purpose: it
 * is where the cart is created, so it cannot require one.
 */
const CART_BACKED: ReadonlySet<FlowScreenId> = new Set<FlowScreenId>(['S-07', 'S-08', 'S-09']);

/**
 * What each screen needs before it means anything.
 *
 * Screens absent from this switch have no prerequisite — S-03 is the first step, and
 * whether a device screen belongs in the journey at all is decided by the variant
 * rather than by anything in this switch.
 */
const isSatisfied = (
  screen: FlowScreenId,
  draft: MigrationDraftStore,
  cart: CartStore,
): boolean => {
  switch (screen) {
    // An offer. Every screen after S-03 prices something against one: the handset
    // list is filtered by `deviceEligible`, and the instalment check needs the
    // commitment term.
    case 'S-04':
    case 'S-11':
      return draft.offerId() !== null;

    // A code already sent. The number alone is not enough: S-05 has nothing to
    // verify against without a challenge, and requesting one on arrival would mean
    // a page load sending an SMS — which, with sends unthrottled (Q-24), makes
    // reloading the screen a way to text a colleague repeatedly. So an arrival with
    // no live challenge goes back to S-04, where sending is a deliberate press.
    case 'S-05':
      return draft.otpChallengeId() !== null;

    // A number the customer proved they hold. Everything from the SIM choice onwards
    // is an order against that number, so this is the line that matters most.
    case 'S-06':
      return draft.verifiedMsisdn() !== null;

    // Everything S-06 required, plus S-06's own answer. The SIM option is what
    // creates the cart — `AddToCartInput.simOption` is non-null — so a cart with one
    // on it is proof the offer, the device and the instalment plan went in with it.
    // Checking the option rather than the cart's existence matters: `verifyOtp`
    // creates an empty cart record, so a cart alone proves only that the OTP cleared.
    case 'S-07':
    case 'S-08':
      return draft.verifiedMsisdn() !== null && cart.simOption() !== null;

    // S-09 additionally requires what `placeOrder` requires, which is now settled
    // rather than guessed: `OrderService.assertCartComplete` rejects a cart with no
    // payment method, and one with no address when something ships. Those are S-08's
    // two answers, so an arrival here without them goes back to S-08 — where the
    // fields are — instead of rendering a confirmation for an order that cannot be
    // placed.
    case 'S-09':
      if (draft.verifiedMsisdn() === null || cart.simOption() === null) return false;
      if (cart.paymentMethod() === null) return false;
      return !cart.requiresDelivery() || cart.deliveryAddress() !== null;

    // The instalment screen prices a device. Without one there is nothing to price.
    case 'S-13':
      return draft.pendingDeviceId() !== null;

    default:
      return true;
  }
};

export const draftGuard: CanActivateFn = async (route, state) => {
  // Every injection happens here, before the first `await` — outside the injection
  // context an `inject()` call throws, and the context ends at the first suspension.
  const access = inject(AccessBlockStore);
  const draft = inject(MigrationDraftStore);
  const cart = inject(CartStore);
  const router = inject(Router);

  // `sessionGuard` returns true when it has resolved a block, so this guard still
  // runs. A blocked customer must keep the explanation — bouncing them to an earlier
  // step because their draft is empty would replace a stated reason with a silent
  // redirect, and their draft is empty precisely because they never got in.
  if (access.isBlockedAt(state.url)) return true;

  const redirect = (to: FlowScreenId): RedirectCommand =>
    new RedirectCommand(router.parseUrl(ROUTE_FOR[to]));

  // Bracket access is forced by `noPropertyAccessFromIndexSignature` (TS4111) —
  // see the same note in `shell.component.ts`.
  const screen = (route.data['meta'] as PageMeta | undefined)?.screen;
  if (screen === undefined) return true;

  const variant = draft.effectiveVariant();

  // S-12 is not in the sequence — it hangs off the device list it was opened from,
  // sharing S-11's step — so it is asked and answered before the order is consulted.
  if (screen === 'S-12') {
    return draft.pendingDeviceId() !== null ? true : redirect('S-11');
  }

  const order = screenOrder(variant);
  const requested = order.indexOf(screen as FlowScreenId);

  // Not in this journey's order at all: a device screen on a SIM-only draft. S-03 is
  // where the variant is chosen, so that is where the answer has to come from.
  if (requested < 0) return redirect('S-03');

  // Only the screens that ask something of the cart wait for it. See the header.
  if (CART_BACKED.has(screen as FlowScreenId)) await cart.ensureLoaded();

  // A cart-backed screen further down the order reads as unsatisfied while the cart
  // is unloaded, which is harmless: `requested` is earlier than it, so the comparison
  // below lets the navigation through regardless.
  const firstUnmet = order.findIndex((s) => !isSatisfied(s, draft, cart));
  if (firstUnmet < 0 || requested < firstUnmet) return true;

  const blocker = order[firstUnmet];
  if (blocker === undefined) return true;

  // The screen *before* the first unsatisfied one — that is where the missing thing
  // is supplied. Redirecting to the unsatisfied screen itself would bounce the
  // customer straight back here.
  const target = previousScreen(variant, blocker);
  return target === null ? true : redirect(target);
};
